import { BaseTool } from './BaseTool';
import { ToolType } from '../types';

export class CropTool extends BaseTool {
  public id: ToolType = 'crop';
  public name = 'Crop Tool';
  public cursor = 'crosshair';

  public cropX = 0;
  public cropY = 0;
  public cropW = 0;
  public cropH = 0;

  private isDragging = false;
  private dragHandle: string | null = null;
  private startMouseX = 0;
  private startMouseY = 0;
  private initialCrop = { x: 0, y: 0, w: 0, h: 0 };

  public onActivate(): void {
    this.cropX = 0;
    this.cropY = 0;
    this.cropW = this.editor.width;
    this.cropH = this.editor.height;
    this.applyAspectRatio();
    this.editor.render();
  }

  public applyAspectRatio(): void {
    const ratioStr = this.editor.toolSettings.cropAspect;
    if (ratioStr === 'free') return;

    let targetRatio = 1;
    if (ratioStr === '1:1') targetRatio = 1;
    else if (ratioStr === '4:3') targetRatio = 4 / 3;
    else if (ratioStr === '16:9') targetRatio = 16 / 9;
    else if (ratioStr === '9:16') targetRatio = 9 / 16;
    else if (ratioStr === '3:2') targetRatio = 3 / 2;

    const currentW = this.cropW;
    const currentH = this.cropH;

    if (currentW / currentH > targetRatio) {
      this.cropW = Math.round(currentH * targetRatio);
    } else {
      this.cropH = Math.round(currentW / targetRatio);
    }
  }

  public onMouseDown(e: MouseEvent, canvasX: number, canvasY: number): void {
    this.isDragging = true;
    this.startMouseX = canvasX;
    this.startMouseY = canvasY;
    this.initialCrop = { x: this.cropX, y: this.cropY, w: this.cropW, h: this.cropH };

    // Check if clicked near a corner/edge handle
    const handleThreshold = 15 / this.editor.zoom;
    const nearLeft = Math.abs(canvasX - this.cropX) < handleThreshold;
    const nearRight = Math.abs(canvasX - (this.cropX + this.cropW)) < handleThreshold;
    const nearTop = Math.abs(canvasY - this.cropY) < handleThreshold;
    const nearBottom = Math.abs(canvasY - (this.cropY + this.cropH)) < handleThreshold;

    if (nearTop && nearLeft) this.dragHandle = 'tl';
    else if (nearTop && nearRight) this.dragHandle = 'tr';
    else if (nearBottom && nearLeft) this.dragHandle = 'bl';
    else if (nearBottom && nearRight) this.dragHandle = 'br';
    else if (nearTop) this.dragHandle = 't';
    else if (nearBottom) this.dragHandle = 'b';
    else if (nearLeft) this.dragHandle = 'l';
    else if (nearRight) this.dragHandle = 'r';
    else if (
      canvasX > this.cropX &&
      canvasX < this.cropX + this.cropW &&
      canvasY > this.cropY &&
      canvasY < this.cropY + this.cropH
    ) {
      this.dragHandle = 'move';
    } else {
      // Start a new crop rectangle
      this.cropX = canvasX;
      this.cropY = canvasY;
      this.cropW = 0;
      this.cropH = 0;
      this.dragHandle = 'br';
    }
  }

  public onMouseMove(_e: MouseEvent, canvasX: number, canvasY: number): void {
    if (!this.isDragging || !this.dragHandle) return;

    const dx = canvasX - this.startMouseX;
    const dy = canvasY - this.startMouseY;

    if (this.dragHandle === 'move') {
      this.cropX = Math.max(0, Math.min(this.editor.width - this.cropW, this.initialCrop.x + dx));
      this.cropY = Math.max(0, Math.min(this.editor.height - this.cropH, this.initialCrop.y + dy));
    } else if (this.dragHandle === 'br') {
      this.cropW = Math.max(10, this.initialCrop.w + dx);
      this.cropH = Math.max(10, this.initialCrop.h + dy);
      this.applyAspectRatio();
    } else if (this.dragHandle === 'tl') {
      const newX = Math.max(0, this.initialCrop.x + dx);
      const newY = Math.max(0, this.initialCrop.y + dy);
      this.cropW = Math.max(10, this.initialCrop.w - (newX - this.initialCrop.x));
      this.cropH = Math.max(10, this.initialCrop.h - (newY - this.initialCrop.y));
      this.cropX = newX;
      this.cropY = newY;
    } else if (this.dragHandle === 'tr') {
      const newY = Math.max(0, this.initialCrop.y + dy);
      this.cropW = Math.max(10, this.initialCrop.w + dx);
      this.cropH = Math.max(10, this.initialCrop.h - (newY - this.initialCrop.y));
      this.cropY = newY;
    } else if (this.dragHandle === 'bl') {
      const newX = Math.max(0, this.initialCrop.x + dx);
      this.cropW = Math.max(10, this.initialCrop.w - (newX - this.initialCrop.x));
      this.cropH = Math.max(10, this.initialCrop.h + dy);
      this.cropX = newX;
    }

    this.editor.renderOverlayOnly();
  }

  public onMouseUp(_e: MouseEvent): void {
    this.isDragging = false;
    this.dragHandle = null;
  }

  public onKeyDown(e: KeyboardEvent): void {
    if (e.key === 'Enter') {
      this.applyCrop();
    } else if (e.key === 'Escape') {
      this.cancelCrop();
    }
  }

  public applyCrop(): void {
    if (this.cropW <= 0 || this.cropH <= 0) return;
    this.editor.crop(
      Math.round(this.cropX),
      Math.round(this.cropY),
      Math.round(this.cropW),
      Math.round(this.cropH)
    );
    this.editor.setTool('move');
  }

  public cancelCrop(): void {
    this.editor.setTool('move');
  }

  public renderOverlay(ctx: CanvasRenderingContext2D): void {
    const zoom = this.editor.zoom;
    const panX = this.editor.panX;
    const panY = this.editor.panY;

    const sx = this.cropX * zoom + panX;
    const sy = this.cropY * zoom + panY;
    const sw = this.cropW * zoom;
    const sh = this.cropH * zoom;

    const canvasW = this.editor.width * zoom;
    const canvasH = this.editor.height * zoom;

    ctx.save();

    // Dark dimmed overlay outside crop box
    ctx.fillStyle = 'rgba(0, 0, 0, 0.65)';
    // Top
    ctx.fillRect(panX, panY, canvasW, Math.max(0, sy - panY));
    // Bottom
    ctx.fillRect(panX, sy + sh, canvasW, Math.max(0, (panY + canvasH) - (sy + sh)));
    // Left
    ctx.fillRect(panX, sy, Math.max(0, sx - panX), sh);
    // Right
    ctx.fillRect(sx + sw, sy, Math.max(0, (panX + canvasW) - (sx + sw)), sh);

    // Crop box outline
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 1.5;
    ctx.strokeRect(sx, sy, sw, sh);

    // Rule of thirds grid
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.35)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    // Vertical grid lines
    ctx.moveTo(sx + sw / 3, sy);
    ctx.lineTo(sx + sw / 3, sy + sh);
    ctx.moveTo(sx + (sw * 2) / 3, sy);
    ctx.lineTo(sx + (sw * 2) / 3, sy + sh);
    // Horizontal grid lines
    ctx.moveTo(sx, sy + sh / 3);
    ctx.lineTo(sx + sw, sy + sh / 3);
    ctx.moveTo(sx, sy + (sh * 2) / 3);
    ctx.lineTo(sx + sw, sy + (sh * 2) / 3);
    ctx.stroke();

    // Handles at 4 corners
    const hSize = 8;
    ctx.fillStyle = '#ffffff';
    ctx.strokeStyle = '#1e293b';
    ctx.lineWidth = 1.5;

    const corners = [
      [sx, sy],
      [sx + sw, sy],
      [sx, sy + sh],
      [sx + sw, sy + sh]
    ];
    corners.forEach(([cx, cy]) => {
      ctx.fillRect(cx - hSize / 2, cy - hSize / 2, hSize, hSize);
      ctx.strokeRect(cx - hSize / 2, cy - hSize / 2, hSize, hSize);
    });

    ctx.restore();
  }
}
